/**
 * TabsKidRenderer — a KidRenderingPlugin for block.type === 'tabs'
 *
 * This is a DUPLICATE of the KidsAsTabs component extracted into the
 * plugin architecture. It demonstrates the KidRenderingPlugin interface:
 *
 *   { Icon, Root, SingleKid }
 *
 * Where:
 *   - Icon       icon component for the BlockSettings toggle button
 *   - Root       <wa-tab-group> wrapper that iterates children
 *   - SingleKid  renders one child as <wa-tab> + <wa-tab-panel>
 *                with SortableBlocks(TreeItem) inside the panel
 */

import type { KidRenderingPlugin, SingleBlockRenderer } from '../types'
import type { SortableBlockItem } from '../../components/SortableBlocks'
import { Logger } from 'besonders-logger'
import { For, mapArray } from 'solid-js'
import { useBlk } from '../../data/VMs/BlockVM'
import { useKidRelations } from '../../ui/reactive'
import { Iconify } from '../../components/mini-components'
import { SortableBlocks } from '../../components/SortableBlocks'
import { TreeItem } from '../../components/TreeItem'
import classNames from 'classnames'

const { DEBUG, WARN } = Logger.setup(Logger.DEBUG) // eslint-disable-line unused-imports/no-unused-vars

// ────────────────────────────────────────────────────────────────────
// SingleKid renderer — renders ONE child as a tab + tab-panel
// ────────────────────────────────────────────────────────────────────
//
// Each child looks up its own children (grandkids of the parent) and
// renders them inside the tab panel render prop via SortableBlocks+TreeItem.
// This mirrors the exact logic from KidsAsTabs.tsx.

const tabsSingleKidRenderer: SingleBlockRenderer = {
  name: 'tabs-kid',
  Renderer: (props) => {
    const item = props.item
    const blockVM = useBlk(item.blockID)
    DEBUG.force('[plugin:tabs] SingleKid rendering', { blockID: item.blockID, tab: blockVM.contentPlaintext })
    const kidRelations = item.blockID ? useKidRelations(item.blockID) : undefined
    const kidsForThisTab = kidRelations
      ? mapArray(kidRelations, (rel) => {
        const block = useBlk(rel.block)
        if (!block) WARN('[tabs-kid-renderer] rel refers to missing block', { block, rel })
        DEBUG('[tabs-kid-renderer] kidItemsFromRelations', { rel, blockID: item.blockID })
        return ({
          id: rel.en,
          relationID: rel.en,
          blockID: rel.block,
        }) satisfies SortableBlockItem
      })
      : () => undefined

    return (
      <>
        <wa-tab slot='nav' panel={blockVM.contentPlaintext}>
          {blockVM.contentPlaintext}
        </wa-tab>
        <wa-tab-panel name={blockVM.contentPlaintext}>
          <SortableBlocks
            class='rounded py-2 pr-1'
            blockID={item.blockID}
            items={kidsForThisTab()}
          >
            {(kidItem) => {
              DEBUG('[tabs-kid-renderer] TreeItem:', kidItem)
              return <TreeItem {...kidItem} />
            }}
          </SortableBlocks>
        </wa-tab-panel>
      </>
    )
  },
}

// ────────────────────────────────────────────────────────────────────
// Root renderer — the outer <wa-tab-group> container
// ────────────────────────────────────────────────────────────────────
//
// Iterates over all immediate children and delegates each one to
// the tabsSingleKidRenderer defined above.

const TabsRoot: KidRenderingPlugin['Root'] = (props) => {
  DEBUG.force('[plugin:tabs] TabsRoot rendering', {
    blockID: props.blockID,
    count: props.items?.length,
  })
  return (
    <wa-tab-group class={classNames('w-88% self-center p-2')}>
      <For each={props.items}>
        {(item) => <tabsSingleKidRenderer.Renderer item={item} blockID={props.blockID} />}
      </For>
    </wa-tab-group>
  )
}

// ────────────────────────────────────────────────────────────────────
// Plugin definition
// ────────────────────────────────────────────────────────────────────
//
// Export this object and register it in init.ts via:
//   registerKidRenderingPlugin(pluginKidsAsTabs)
//
// The TreeItem dispatcher will then use plugin.Root when
// block.type === 'tabs' and the plugin is registered.

export const pluginKidsAsTabs: KidRenderingPlugin = {
  type: 'tabs',
  displayName: 'Tabs',
  Icon: () => <Iconify name='tabs' scale={120} />,
  Root: TabsRoot,
  SingleKid: tabsSingleKidRenderer,
}
